import { z } from 'zod'

/** An integer amount of money in minor units. Rejects floats outright. */
export const minorSchema = z.number().int()
/** A non-negative integer money amount. */
export const minorNonNegativeSchema = z.number().int().min(0)
/** A quantity in milli-units. Must be positive: a zero-quantity line is a mistake. */
export const milliSchema = z.number().int().min(1)
/** Basis points, 0-10000 (0%-100%). */
export const basisPointsSchema = z.number().int().min(0).max(10_000)

export const idSchema = z.string().min(1)
export const isoDateTimeSchema = z.string().datetime({ offset: true }).or(z.string().min(1))

/** Trimmed non-empty string with a maximum, the default shape for user text. */
export const text = (max = 255) => z.string().trim().min(1).max(max)
/** Optional user text: empty string and whitespace normalise to null. */
export const optionalText = (max = 255) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .default(null)

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
})
export type Pagination = z.infer<typeof paginationSchema>

export interface Paginated<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

/** The error envelope every failing API response uses. */
export interface ApiErrorBody {
  error: {
    code:
      | 'validation_error'
      | 'not_found'
      | 'conflict'
      | 'internal_error'
      | 'payload_too_large'
      /** A machine endpoint was called without a valid API key. */
      | 'unauthorized'
      /** A staff request had no valid session cookie — sign in again. */
      | 'session_required'
      /** Signed in, but this account's role does not permit the action. */
      | 'forbidden'
      /** The endpoint exists but the deployment has not been given what it needs. */
      | 'not_configured'
      /** Too many failed login attempts; request blocked temporarily. */
      | 'too_many_requests'
    message: string
    details?: unknown
  }
}
